'use client';

import { useMemo, useRef } from 'react';
import { LogLevel, type LogProps } from '@cabal/shared/util/seq-log';
import { IS_SEQ_LOG_ENABLED, useSeqLog } from './use-seq-log';

// Metadata only ever reaches Seq. When the transport is off the returned logger
// is a no-op, so serializing (and re-parsing) caller metadata on every render
// buys nothing — mirrors the same guard in `useSeqLog`.
const EMPTY_METADATA_SIGNATURE = '{}';

export enum PersistentDebugAsyncPhase {
  Start = 'start',
  Resolve = 'resolve',
  Reject = 'reject',
  Cancel = 'cancel',
}

enum PersistentDebugKind {
  Marker = 'marker',
  EventFlow = 'event-flow',
  StateTransition = 'state-transition',
  BranchChoice = 'branch-choice',
  AsyncBoundary = 'async-boundary',
  Error = 'error',
}

enum PersistentDebugScope {
  PersistentDebugLog = 'persistent-debug-log',
}

type PersistentDebugPayload = Record<string, unknown>;

interface UsePersistentDebugLoggerInput {
  component: string;
  instanceId: string;
  metadata?: PersistentDebugPayload;
}

interface PersistentDebugLogInput {
  name: string;
  payload?: PersistentDebugPayload;
}

interface PersistentDebugStateTransitionInput extends PersistentDebugLogInput {
  from: unknown;
  to: unknown;
}

interface PersistentDebugBranchChoiceInput extends PersistentDebugLogInput {
  selectedBranch: string;
}

interface PersistentDebugAsyncBoundaryInput extends PersistentDebugLogInput {
  phase: PersistentDebugAsyncPhase;
}

interface PersistentDebugErrorInput extends PersistentDebugLogInput {
  error: unknown;
}

export interface PersistentDebugLogger {
  logMarker: (input: PersistentDebugLogInput) => void;
  logEventFlow: (input: PersistentDebugLogInput) => void;
  logStateTransition: (input: PersistentDebugStateTransitionInput) => void;
  logBranchChoice: (input: PersistentDebugBranchChoiceInput) => void;
  logAsyncBoundary: (input: PersistentDebugAsyncBoundaryInput) => void;
  logError: (input: PersistentDebugErrorInput) => void;
}

function normalizeError(error: unknown): PersistentDebugPayload {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack ?? null,
    };
  }

  return { value: String(error) };
}

export function usePersistentDebugLogger({
  component,
  instanceId,
  metadata,
}: UsePersistentDebugLoggerInput): PersistentDebugLogger {
  const sequenceRef = useRef(0);
  const serializedMetadata = IS_SEQ_LOG_ENABLED
    ? JSON.stringify(metadata ?? {})
    : EMPTY_METADATA_SIGNATURE;
  const metadataProps = useMemo<PersistentDebugPayload>(
    () => JSON.parse(serializedMetadata) as PersistentDebugPayload,
    [serializedMetadata],
  );
  const context = useMemo<LogProps>(() => ({
    scope: PersistentDebugScope.PersistentDebugLog,
    instanceId,
    ...metadataProps,
  }), [instanceId, metadataProps]);
  const log = useSeqLog(component, context);

  return useMemo<PersistentDebugLogger>(() => {
    const emit = (
      kind: PersistentDebugKind,
      input: PersistentDebugLogInput,
      level: LogLevel = LogLevel.Info,
      extra: PersistentDebugPayload = {},
    ): void => {
      sequenceRef.current += 1;
      log(input.name, {
        kind,
        sequence: sequenceRef.current,
        name: input.name,
        ...(input.payload ? { payload: input.payload } : {}),
        ...extra,
      }, level);
    };

    return {
      logMarker: (input: PersistentDebugLogInput): void => {
        emit(PersistentDebugKind.Marker, input);
      },
      logEventFlow: (input: PersistentDebugLogInput): void => {
        emit(PersistentDebugKind.EventFlow, input);
      },
      logStateTransition: (
        input: PersistentDebugStateTransitionInput,
      ): void => {
        emit(PersistentDebugKind.StateTransition, input, LogLevel.Info, {
          from: input.from,
          to: input.to,
        });
      },
      logBranchChoice: (input: PersistentDebugBranchChoiceInput): void => {
        emit(PersistentDebugKind.BranchChoice, input, LogLevel.Info, {
          selectedBranch: input.selectedBranch,
        });
      },
      logAsyncBoundary: (
        input: PersistentDebugAsyncBoundaryInput,
      ): void => {
        emit(PersistentDebugKind.AsyncBoundary, input, LogLevel.Info, {
          phase: input.phase,
        });
      },
      logError: (input: PersistentDebugErrorInput): void => {
        emit(PersistentDebugKind.Error, input, LogLevel.Error, {
          error: normalizeError(input.error),
        });
      },
    };
  }, [log]);
}
